<template>
	<CardGeneric class="text-left max-sm:pb-4 paymentInfoComponent">
		<p class="cardTitle">{{ $t("paymentMethodTitle") }}</p>
		<p class="cardSubTitle">{{ $t("paymentMethodSubtitle") }}</p>

		<div class="grid grid-cols-2 gap-y-5 gap-x-7">
			<div class="col-span-2 inputFieldContainer">
				<label
					for="CardNumber"
					class="inputFieldLabel text-primary"
					>{{ $t("cardNumber") }}
					<FormRequiredAsterisk :required="true" />
				</label>
				<div
					id="CardNumber"
					class="inputField"></div>
				<label
					for="CardNumber"
					v-if="cardNumberInvalid"
					class="block mb-1 text-xs text-red-500"
					>{{ $t("invalidCardNumber") }}</label
				>
			</div>

			<div class="inputFieldContainer">
				<label
					for="ExpiryDate"
					class="inputFieldLabel text-primary"
					>{{ $t("cardExpirationDate") }}
					<FormRequiredAsterisk :required="true" />
				</label>
				<div
					id="ExpiryDate"
					class="inputField bg-white"></div>
				<label
					for="ExpiryDate"
					v-if="expiryDateInvalid"
					class="block mb-1 text-xs text-red-500"
					>{{ $t("invalidExpiryDate") }}</label
				>
			</div>

			<div class="inputFieldContainer">
				<label
					for="Cvv"
					class="inputFieldLabel text-primary"
					>{{ $t("cardCVVCode") }}
					<FormRequiredAsterisk :required="true" />
				</label>
				<div
					id="Cvv"
					class="inputField bg-white"></div>
				<label
					for="Cvv"
					v-if="cvvInvalid"
					class="block mb-1 text-xs text-red-500"
					>{{ $t("invalidCVV") }}</label
				>
			</div>
		</div>

		<CardDivider
			v-if="runtimeConfig.public.ENABLE_COUPON_CODES"
			color="border-secondary my-5" />

		<CheckoutApplyCoupon />
	</CardGeneric>
</template>

<script setup>
	//Paysafe Specific logic until we have a better idea of what other payment providers will look like
	//https://developer.paysafe.com/en/api-docs/client-side-sdks/paysafejs-powered-by-cards-api/overview/
	import { useI18n } from "vue-i18n"
	import { useCartStore } from "~/stores/CartStore"
	import { usePaysafeStore } from "~/stores/PaysafeStore"
	import { useNuxtApp } from "#app"
	import { ENABLE_PLACE_ORDER, PAYSAFE_TOKENIZE, PAYSAFE_TOKENIZED } from "~/assets/events/checkout.js"
	import { SHOW_ERROR } from "assets/events/errors.js"
	import { HIDE_API_LOADING, SHOW_API_LOADING } from "assets/events/api.js"

	const runtimeConfig = useRuntimeConfig()
	const { t } = useI18n()
	const cartStore = useCartStore()
	const paysafeStore = usePaysafeStore()
	const { $eventBus, $countryAndStatesService } = useNuxtApp()

	const paysafeInstance = ref(null)
	const cardNumberInvalid = ref(false)
	const expiryDateInvalid = ref(false)
	const cvvInvalid = ref(false)

	const paysafeFields = {
		fields: {
			CardNumber: {
				selector: "#CardNumber",
				placeholder: t("cardNumber"),
				separator: " ",
				optional: false,
				hasErrorField: (value) => (cardNumberInvalid.value = value),
			},
			ExpiryDate: {
				selector: "#ExpiryDate",
				placeholder: t("cardExpiryDatePlaceholder"),
				optional: false,
				hasErrorField: (value) => (expiryDateInvalid.value = value),
			},
			Cvv: {
				selector: "#Cvv",
				placeholder: t("cardCvvPlaceholder"),
				optional: false,
				hasErrorField: (value) => (cvvInvalid.value = value),
			},
		},
	}

	const setFieldFocus = (eventInstance, event) => {
		event.target.containerElement.classList.add("inputFieldFocused")
	}

	const handleFieldBlur = (eventInstance, event) => {
		event.target.containerElement.classList.remove("inputFieldFocused")

		const fieldId = event.target.fieldName
		validateField(fieldId)
	}

	const validateField = (fieldId) => {
		const isFieldValid = paysafeInstance.value.fields(fieldId).isValid()

		if (isFieldValid) {
			const el = document.getElementById(fieldId)
			el && el.classList.remove("inputFieldError")

			const paysafeObj = paysafeFields.fields[fieldId]
			paysafeObj.hasErrorField(false)
		} else {
			const el = document.getElementById(fieldId)
			el && el.classList.add("inputFieldError")

			const paysafeObj = paysafeFields.fields[fieldId]
			paysafeObj.hasErrorField(true)
		}

		return isFieldValid
	}

	//https://developer.paysafe.com/en/api-docs/client-side-sdks/paysafejs-powered-by-cards-api/tokenize/
	const tokenizeCard = () => {
		if (!paysafeInstance.value) {
			$eventBus.$emit(SHOW_ERROR, {
				titleText: `${t("errorPageTitle")}`,
				bodyText: `Error while initializing the payment provider`,
				btnText: `${t("refresh")}`,
				closeCallback: reloadNuxtApp,
			})
		}

		const tokenizeOptions = {
			vault: {
				holderName: cartStore.billingFirstName + " " + cartStore.billingLastName,
				billingAddress: {
					country: $countryAndStatesService.$convertIso3ToIso2(cartStore.billingCountry),
					zip: cartStore.billingZipcode,
					state: cartStore.billingState,
					city: cartStore.billingCity,
					street: cartStore.billingAddress1,
					street2: cartStore.billingAddress2,
				},
			},
		}

		$eventBus.$emit(SHOW_API_LOADING)
		paysafeInstance.value.tokenize(tokenizeOptions, (instance, error, result) => {
			$eventBus.$emit(HIDE_API_LOADING)
			if (error) {
				$eventBus.$emit(SHOW_ERROR, {
					titleText: "Card Error",
					bodyText: "Please check your address & card info and try again!",
				})
				console.warn("Error Tokenizing Card - Payload: " + JSON.stringify(tokenizeOptions))
				console.warn(JSON.stringify(error))
				$eventBus.$emit(ENABLE_PLACE_ORDER)
			} else {
				paysafeStore.setToken(result.token)
				$eventBus.$emit(PAYSAFE_TOKENIZED)
			}
		})
	}

	const initializePaysafe = () => {
		const API_KEY = runtimeConfig.public.paysafe.PAYSAFE_CLIENT_KEY
		const options = {
			environment: runtimeConfig.public.paysafe.PAYSAFE_CLIENT_ENV,
			// set the CSS selectors to identify the payment field divs above
			// set the placeholder text to display in these fields
			fields: paysafeFields.fields,
		}

		// initalize the hosted iframes using the SDK setup function
		// the 'paysafe' variable is initialized from the paysafe sdk in the script tag
		paysafe.fields.setup(API_KEY, options, (instance, error) => {
			paysafeInstance.value = instance
			if (error) {
				const displayErrorIfOnCheckoutPage = useRoute().path.indexOf("checkout") >= 0
				if (displayErrorIfOnCheckoutPage) {
					$eventBus.$emit(SHOW_ERROR, {
						titleText: `${t("errorPageTitle")}`,
						bodyText: `Error while initializing the payment provider`,
						closeBtnText: `${t("refresh")}`,
						closeCallback: reloadNuxtApp,
					})
				}
			} else {
				instance.fields("CardNumber ExpiryDate Cvv").focus(setFieldFocus)
				instance.fields("CardNumber ExpiryDate Cvv").blur(handleFieldBlur)
			}
		})
	}

	onMounted(() => {
		//The Tokenize event is triggered by the checkout page and handled here
		$eventBus.$on(PAYSAFE_TOKENIZE, () => {
			tokenizeCard()
		})

		//Download the latest paysafe sdk on checkout page load and initialize paysafe
		const script = document.createElement("script")
		script.src = "https://hosted.paysafe.com/js/v1/latest/paysafe.min.js"
		script.async = true
		script.onload = initializePaysafe
		document.head.appendChild(script)
	})

	onUnmounted(() => {
		$eventBus.$off(PAYSAFE_TOKENIZE)
	})

	//Called by Checkout handler to validate fields before submitting the order
	const isPaymentInfoValid = () => {
		let fieldsAreValid = true
		Object.keys(paysafeFields.fields).forEach((fieldId) => (fieldsAreValid &= validateField(fieldId)))
		return fieldsAreValid
	}

	defineExpose({
		isPaymentInfoValid,
	})
</script>

<style scoped>
	/* inputFieldFocused class dynamically added in js since tailwind helpers do not work here */
	.inputFieldFocused {
		@apply border-secondary border-2;
	}
</style>
